import React, { useEffect, useState } from "react";
import axios from "axios";
import { Form, Select, DatePicker, Button, Table, message, Card, Typography, Space, Tag, Dropdown, Modal } from "antd";
import { MoreOutlined, EditOutlined, PoweroffOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./VrmMapping.css";

const { Title, Text } = Typography;
const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL;

const VrmMapping = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  
  const [vrmUsers, setVrmUsers] = useState([]);
  const [caraAdmins, setCaraAdmins] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [vrmRes, adminRes, mapRes] = await Promise.all([
        axios.get(`${API_URL}api/vrm/users/`, { withCredentials: true }),
        axios.get(`${API_URL}api/vrm/admins/`, { withCredentials: true }),
        axios.get(`${API_URL}api/vrm/mappings/`, { withCredentials: true }),
      ]);

      setVrmUsers(vrmRes.data?.vrm_users || []);
      setCaraAdmins(adminRes.data?.admins || []);
      setMappings(mapRes.data?.mappings || []);
    } catch (error) {
      message.error("Failed to load VRM mapping data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (values) => {
    try {
      const payload = {
        vrm_user_id: values.vrm_user_id,
        admin_id: values.admin_id,
        status: values.status,
        access_start_date: values.access_start_date ? values.access_start_date.format("YYYY-MM-DD") : null,
        access_end_date: values.access_end_date ? values.access_end_date.format("YYYY-MM-DD") : null,
      };

      await axios.post(
        `${API_URL}api/vrm/mappings/create/`,
        payload,
        { withCredentials: true }
      );
      message.success("Mapping saved successfully.");
      form.resetFields();
      await loadData();
    } catch (error) {
      message.error("Failed to save mapping.");
    }
  };

  const handleEditClick = (record) => {
    setEditingMapping(record);
    editForm.setFieldsValue({
      status: record.status,
      access_start_date: record.access_start_date ? dayjs(record.access_start_date) : null,
      access_end_date: record.access_end_date ? dayjs(record.access_end_date) : null,
    });
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await axios.post(
        `${API_URL}api/vrm/mappings/status/`,
        {
          vrm_user_id: editingMapping.vrm_user_id,
          admin_id: editingMapping.admin_id,
          status: values.status,
          access_start_date: values.access_start_date ? values.access_start_date.format("YYYY-MM-DD") : null,
          access_end_date: values.access_end_date ? values.access_end_date.format("YYYY-MM-DD") : null,
        },
        { withCredentials: true }
      );
      message.success("Mapping updated successfully.");
      setIsEditModalVisible(false);
      await loadData();
    } catch (error) {
      message.error("Failed to update mapping.");
    }
  };

  const handleToggleStatus = async (mapping) => {
    const newStatus = String(mapping.status).toLowerCase() === "active" ? "inactive" : "active";
    try {
      await axios.post(
        `${API_URL}api/vrm/mappings/status/`,
        {
          vrm_user_id: mapping.vrm_user_id,
          admin_id: mapping.admin_id,
          status: newStatus,
        },
        { withCredentials: true }
      );
      message.success(`Status changed to ${newStatus}`);
      await loadData();
    } catch (error) {
      message.error("Failed to update mapping status.");
    }
  };

  const columns = [
    {
      title: "VRM User",
      dataIndex: "vrm_name",
      key: "vrm_name",
      render: (text, record) => text || record.vrm_user_id,
    },
    {
      title: "Company (CARA Admin)",
      dataIndex: "company_name",
      key: "company_name",
      render: (text, record) => text || record.admin_id,
    },
    {
      title: "Start Date",
      dataIndex: "access_start_date",
      key: "access_start_date",
      render: (text) => text || "--",
    },
    {
      title: "End Date",
      dataIndex: "access_end_date",
      key: "access_end_date",
      render: (text) => text || "--",
    },
    {
      title: "Tenant DB",
      dataIndex: "tenant_db_name",
      key: "tenant_db_name",
      render: (text) => text || "--",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "volcano"}>
          {status ? status.toUpperCase() : "UNKNOWN"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const items = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleEditClick(record),
          },
          {
            key: 'toggle',
            icon: <PoweroffOutlined />,
            label: String(record.status).toLowerCase() === "active" ? "Deactivate" : "Activate",
            danger: String(record.status).toLowerCase() === "active",
            onClick: () => handleToggleStatus(record),
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper" style={{ padding: "24px" }}>
      <main id="vrm_mapping" className="vrm-mapping-page">
        <Space direction="vertical" size="large" style={{ display: "flex" }}>
          
          <div>
            <Title level={3}>VRM Mapping</Title>
            <Text type="secondary">Assign VRM users to CARA Admin companies and manage access dates.</Text>
          </div>

          <Card title="Create New Mapping">
            <Form 
              form={form} 
              layout="vertical" 
              onFinish={handleCreate}
              initialValues={{ status: "active" }}
            >
              <Form.Item 
                name="vrm_user_id" 
                label="VRM User" 
                rules={[{ required: true, message: "Please select a VRM User!" }]}
              >
                <Select placeholder="Select VRM User" showSearch optionFilterProp="children">
                  {vrmUsers.map((vrm) => (
                    <Option key={vrm.vrm_user_id} value={vrm.vrm_user_id}>
                      {`${vrm.first_name} ${vrm.last_name}`} ({vrm.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item 
                name="admin_id" 
                label="CARA Admin (Company)" 
                rules={[{ required: true, message: "Please select a CARA Admin!" }]}
              >
                <Select placeholder="Select CARA Admin" showSearch optionFilterProp="children">
                  {caraAdmins.map((admin) => (
                    <Option key={admin.admin_id} value={admin.admin_id}>
                      {admin.company_name || `${admin.first_name} ${admin.last_name}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Space size="large" style={{ display: "flex", width: "100%" }}>
                <Form.Item 
                  name="access_start_date" 
                  label="Start Date"
                >
                  <DatePicker format="YYYY-MM-DD" style={{ width: "200px" }} />
                </Form.Item>

                <Form.Item 
                  name="access_end_date" 
                  label="End Date"
                >
                  <DatePicker format="YYYY-MM-DD" style={{ width: "200px" }} />
                </Form.Item>
              </Space>

              <Form.Item 
                name="status" 
                label="Status"
                rules={[{ required: true }]}
              >
                <Select style={{ width: "200px" }}>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Save Mapping
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="Current Mappings">
            <Table 
              columns={columns} 
              dataSource={mappings} 
              rowKey={(record) => `${record.vrm_user_id}-${record.admin_id}`} 
              loading={loading}
              pagination={{ pageSize: 10 }}
              bordered
            />
          </Card>
        </Space>
      </main>

      <Modal
        title="Edit VRM Mapping"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form 
          form={editForm} 
          layout="vertical" 
          onFinish={handleEditSubmit}
        >
          <Form.Item 
            name="access_start_date" 
            label="Start Date"
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          
          <Form.Item 
            name="access_end_date" 
            label="End Date"
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          
          <Form.Item 
            name="status" 
            label="Status" 
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>
          
          <Form.Item style={{ textAlign: "right", marginBottom: 0, marginTop: "24px" }}>
            <Button onClick={() => setIsEditModalVisible(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VrmMapping;
